import { useRouter } from "next/router";
import { useUserData, withUserData } from "@/contexts/UserDataContext";
import { provideUserData } from "@/utils/admin/auth-page";
import { Fragment, useEffect, useState, useCallback } from "react";
import PageInfo from "@/components/PageInfo";
import Navbar from "@/components/Navbar";
import { createSelectOptions, createValueList } from "@/utils/ui/select-helper";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  Heading,
  IconButton,
  Input,
  ListItem,
  OrderedList,
  Spacer,
  Text,
  Textarea,
  chakra,
  useConst,
  useToken,
} from "@chakra-ui/react";
import {
  CurrentQuestionRenderer,
  NextButton,
  Question,
  QuestionAcademicInterests,
  QuestionSetupAccount,
  QuestionTargetColleges,
  QuestionTestScores,
  UserInfoProvider,
  useUserInfo,
} from "@/components/shared/Questionnaire";
import { createCheckeredBackground } from "@/utils/style";
import { ParticlesContainer } from "@/utils/particles";
import {
  AWARD_LEVEL_OPTIONS,
  COLLEGE_OPTIONS_T20,
  DATABASE_SIZE,
  ETHNICITY_OPTIONS,
  RANK_IN_CLASS_OPTIONS,
} from "@/utils/profile";
import { Select } from "chakra-react-select";
import { EmojiBox } from "@/components/shared/generic";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAPI } from "@/contexts/APIContext";

const Intro = () => {
  const api = useAPI();
  const { currentUser } = useUserData();

  useEffect(() => {
    if (currentUser?.email === "atmanj@stanford.edu") {
      api.postUnprotected("users/data", true, {
        satScore: null,
        actScore: null,
        targetColleges: [],
        targetMajor: "",
        rankInClass: "",
        ethnicity: [],
        ecs: [],
        awards: [],
        sex: null,
      });
    }
  }, []);

  return (
    <Question shadowColor="red.200">
      <Box textAlign="left" sx={{ "> p": { fontSize: "lg" } }}>
        <chakra.h1 mt={-4} fontSize={{ base: "4xl", md: "6xl" }} textStyle="pageHeader">
          🎯 How It Works
        </chakra.h1>
        <Text mt={4}>
          We studied <b>{DATABASE_SIZE.applications} real data points</b> from accepted and rejected
          applications to understand what factors matter most in the college admissions process.
          This is the only calculator in the world that uses real data to predict your chances of
          getting into your dream school, and <b>it&apos;s 100% free to use!</b>
        </Text>
        <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }} py={4}>
          Here&apos;s how it works:
        </Heading>
        <OrderedList fontSize="lg" ml={8}>
          <ListItem>Tell us about your stats, awards, and dream schools.</ListItem>
          <ListItem>
            We&apos;ll run your data through our proprietary AI-based algorithm to predict your
            chances of getting in.
          </ListItem>
          <ListItem>
            You&apos;ll receive personalized recommendations to improve your chances of getting in!
          </ListItem>
        </OrderedList>
      </Box>
      <NextButton />
    </Question>
  );
};

const QuestionColleges = () => {
  const shortcuts = [
    {
      name: "The Ivy League",
      colleges: [
        "Harvard University",
        "Princeton University",
        "Yale University",
        "Columbia University in the City of New York",
        "Dartmouth College",
        "University of Pennsylvania",
        "Cornell University",
        "Brown University",
      ],
    },
    {
      name: "HYPSM",
      colleges: [
        "Harvard University",
        "Stanford University",
        "Yale University",
        "Princeton University",
        "Massachusetts Institute of Technology",
      ],
    },
  ];
  return (
    <QuestionTargetColleges
      options={COLLEGE_OPTIONS_T20}
      shortcuts={shortcuts}
      title="🎓 What college should we calculate your acceptance rate for?"
      subtitle="Currently, we only support certain colleges. We're working on adding more, so stay tuned!"
    />
  );
};

const QuestionMajor = () => {
  return (
    <QuestionAcademicInterests
      title="🧬 What major will you be applying for?"
      userKey="targetMajor"
      maxChoices={1}
      allowUndecided
    />
  );
};

const QuestionRankInClass = () => {
  const { registerSkippableQuestion, setKey, userInfo } = useUserInfo();
  const rankInClassOptions = RANK_IN_CLASS_OPTIONS.map((o) => ({ label: o, value: o }));

  const [rankInClass, setRankInClass] = useState(
    createValueList(userInfo.rankInClass ? [userInfo.rankInClass] : [], rankInClassOptions)[0] || {
      label: "Unranked",
      value: "Unranked",
    },
  );
  useEffect(() => setKey("rankInClass", rankInClass.value), [rankInClass]);

  useEffect(() => {
    registerSkippableQuestion("rankInClass");
  }, []);

  return (
    <Question title="🧑‍🎓 What is your rank in your class?">
      <Box>
        <Text my={4} fontSize="lg">
          This is the percentage of students in your class that you rank above. If your school
          doesn&apos;t rank or you don&apos;t know, pick &quot;Unranked.&quot;.
        </Text>
        <Select
          variant="flushed"
          placeholder="Select an option."
          value={rankInClass}
          onChange={setRankInClass}
          options={rankInClassOptions}
        />
        <Flex>
          <Spacer />
          <NextButton />
        </Flex>
      </Box>
    </Question>
  );
};

const QuestionEthnicity = () => {
  const ethnicityOptions = ETHNICITY_OPTIONS.map((x) => ({ label: x, value: x }));
  const { registerSkippableQuestion, userInfo, setKey } = useUserInfo();
  const [ethnicity, setEthnicity] = useState(
    createValueList(userInfo.ethnicity || [], ethnicityOptions),
  );
  useEffect(
    () =>
      setKey(
        "ethnicity",
        ethnicity.map((e) => e.value),
      ),
    [ethnicity],
  );
  useEffect(() => {
    registerSkippableQuestion("ethnicity");
  }, []);

  return (
    <Question title="🌍 What's your ethnicity?">
      <Box>
        <Text my={4} fontSize="lg">
          Colleges like having a diverse student body. If you don&apos;t feel comfortable answering
          this question, you can skip it.
        </Text>
        <Select
          variant="flushed"
          isMulti
          placeholder="Select an option."
          value={ethnicity}
          onChange={setEthnicity}
          options={ethnicityOptions}
        />
        <Flex>
          <Spacer />
          <NextButton />
        </Flex>
      </Box>
    </Question>
  );
};

const QuestionGender = () => {
  const { setKey, onClickNext, registerSkippableQuestion } = useUserInfo();

  const onClick = (gender) => {
    setKey("sex", gender);
    onClickNext();
  };

  useEffect(() => {
    registerSkippableQuestion("sex");
  }, []);

  return (
    <Question title="What gender do you identify as?">
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gridGap={{ base: 2, md: 10 }}
        align="center"
        justify="space-evenly"
      >
        <EmojiBox emoji="🙋‍♀️" label="Female" color="purple.200" onClick={() => onClick("female")} />
        <EmojiBox emoji="🙋‍♂️" label="Male" color="blue.200" onClick={() => onClick("male")} />
        <EmojiBox
          emoji="💁"
          label="Nonbinary"
          color="gray.200"
          onClick={() => onClick("nonbinary")}
        />
      </Grid>
    </Question>
  );
};

const QuestionAwards = () => {
  const { setKey, registerSkippableQuestion, userInfo } = useUserInfo();
  const [awards, setAwards] = useState(userInfo.awards || []);
  const awardLevelOptions = useConst(() => createSelectOptions(AWARD_LEVEL_OPTIONS), []);

  useEffect(() => {
    registerSkippableQuestion("awards");
  }, []);

  useEffect(() => setKey("awards", awards), [awards]);

  return (
    <Question title="🏆 What awards have you won?">
      <Box>
        <Text fontSize="lg" fontStyle="italic" color="gray.500">
          (Optional, but recommended)
        </Text>
        <Text fontSize="lg" mt={4}>
          This can include academic awards, honor societies, and athletics.
        </Text>
        <Flex>
          <Spacer />
          <Button
            colorScheme="green"
            leftIcon={<AddIcon />}
            my={4}
            isDisabled={awards.length >= 5}
            onClick={() => {
              setAwards([...awards, { level: "", description: "" }]);
            }}
          >
            Add Award
          </Button>
        </Flex>
        {awards.length > 0 && (
          <Grid templateColumns="5fr 8fr 1fr" rowGap={1} columnGap={4}>
            <FormLabel fontSize="xl">Award Level</FormLabel>
            <FormLabel fontSize="xl">Description</FormLabel>
            <Box />
            {awards.map(({ description, level }, i) => {
              const onUpdateField = (fieldName, value) => {
                const newAwards = [...awards];
                newAwards[i][fieldName] = value;
                setAwards(newAwards);
              };
              const onDelete = () => {
                const newAwards = [...awards];
                newAwards.splice(i, 1);
                setAwards(newAwards);
              };

              return (
                <Fragment key={i}>
                  <Select
                    value={{ label: level, value: level }}
                    options={awardLevelOptions}
                    placeholder="Level"
                    variant="filled"
                    onChange={(e) => onUpdateField("level", e.value)}
                  />
                  <Input
                    value={description}
                    placeholder="Description"
                    variant="unstyled"
                    onChange={(e) => onUpdateField("description", e.target.value)}
                  />
                  <IconButton
                    colorScheme="red"
                    variant="ghost"
                    icon={<DeleteIcon />}
                    onClick={onDelete}
                  />
                </Fragment>
              );
            })}
          </Grid>
        )}
        <NextButton />
      </Box>
    </Question>
  );
};

const QuestionECs = () => {
  const { setKey, registerSkippableQuestion, userInfo } = useUserInfo();
  const [ecs, setECs] = useState(userInfo.ecs || []);

  useEffect(() => {
    registerSkippableQuestion("ecs");
  }, []);

  useEffect(() => setKey("ecs", ecs), [ecs]);

  return (
    <Question title="🏃🏾‍♂️ What do you do outside of school?">
      <Box>
        <Text fontSize="lg" fontStyle="italic" color="gray.500">
          (Optional, but recommended)
        </Text>
        <Text fontSize="lg">
          This includes clubs, sports, taking care of siblings, working part-time, etc. You can add
          up to 10 activities!
        </Text>
        <Flex>
          <Spacer />
          <Button
            colorScheme="green"
            leftIcon={<AddIcon />}
            my={4}
            isDisabled={ecs.length >= 10}
            onClick={() => {
              setECs([...ecs, { position: "", organization: "", description: "" }]);
            }}
          >
            Add Activity
          </Button>
        </Flex>
        {ecs.length > 0 && (
          <Grid templateColumns="1fr 2fr 2fr 7fr 1fr" rowGap={2} columnGap={1} alignItems="center">
            <Box />
            <FormLabel fontSize="lg">Position</FormLabel>
            <FormLabel fontSize="lg">Organization</FormLabel>
            <FormLabel fontSize="lg">Description</FormLabel>
            <Box />
            {ecs.map(({ position, organization, description }, i) => {
              const onUpdateField = (fieldName, value) => {
                const newECs = [...ecs];
                newECs[i][fieldName] = value;
                setECs(newECs);
              };
              const onDelete = () => {
                const newECs = [...ecs];
                newECs.splice(i, 1);
                setECs(newECs);
              };

              return (
                <Fragment key={i}>
                  <Text fontSize="2xl" justifySelf="end" pr={4}>
                    <sup>#</sup>
                    {i + 1}
                  </Text>
                  <Textarea
                    value={position}
                    fontWeight="bold"
                    placeholder="Position"
                    padding={1}
                    fontSize="sm"
                    onChange={(e) => onUpdateField("position", e.target.value)}
                  />
                  <Textarea
                    value={organization}
                    placeholder="Organization"
                    padding={1}
                    fontWeight="bold"
                    fontSize="sm"
                    onChange={(e) => onUpdateField("organization", e.target.value)}
                  />
                  <Textarea
                    value={description}
                    placeholder="Description"
                    padding={1}
                    fontSize="sm"
                    onChange={(e) => onUpdateField("description", e.target.value)}
                  />
                  <IconButton
                    colorScheme="red"
                    variant="ghost"
                    icon={<DeleteIcon />}
                    onClick={onDelete}
                  />
                </Fragment>
              );
            })}
          </Grid>
        )}
        <NextButton />
      </Box>
    </Question>
  );
};

const QuestionRedirect = () => {
  return <QuestionSetupAccount redirectURL="/profile/acceptance-rate-calculator/results" />;
};

function AcceptanceRateCalculator() {
  const { userData } = useUserData();
  const router = useRouter();

  const bg = createCheckeredBackground("1em", "#cc0000", "11");

  useEffect(() => {
    if (userData.type === "college") {
      router.push("/profile/dashboard");
    }
  }, []);

  return (
    <>
      <PageInfo
        title="Calculate your odds of getting into your dream school"
        description={`Our acceptance rate calculator is based off ${DATABASE_SIZE.applications} real data points from students at Top 20 colleges. Find out your chances of getting into your dream school!`}
      />
      <Box minH="100vh" pb={10} {...bg}>
        <ParticlesContainer />
        <Navbar />
        <Box mt={8} zIndex={1} px={4}>
          <UserInfoProvider pageName="arc" bordered nQuestions={9}>
            <CurrentQuestionRenderer
              components={[
                Intro,
                QuestionColleges,
                QuestionMajor,
                QuestionTestScores,
                QuestionGender,
                QuestionEthnicity,
                QuestionRankInClass,
                QuestionECs,
                QuestionAwards,
                QuestionRedirect,
              ]}
              bg="transparent"
              backdropFilter="none"
            />
          </UserInfoProvider>
        </Box>
      </Box>
    </>
  );
}

export default withUserData(AcceptanceRateCalculator);

export const getServerSideProps = provideUserData();
